import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import html2pdf from 'html2pdf.js';

/* ===== CAPACITOR IMPORTAI ===== */
import { Share } from '@capacitor/share';
import { Filesystem, Directory } from '@capacitor/filesystem'; 

/* ===== FIREBASE IMPORTAI ===== */
import { db, storage } from '../../firebase'; 
import { collection, addDoc, query, where, getDocs, getDoc, doc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useTranslation } from 'react-i18next';
import './WorkerBeweissicherung.css';

/* ===== PAGALBINĖS FUNKCIJOS ===== */
const formatAddress = (addr) => {
    if (!addr) return "";
    if (typeof addr === 'object') {
        const street = addr.street || addr.route || "";
        const city = addr.city || addr.locality || "";
        const zip = addr.zip || addr.postal_code || "";
        return `${street} ${city} ${zip}`.trim();
    }
    return String(addr);
};

const getCurrentDateTimeFormatted = () => {
    const now = new Date();
    const d = String(now.getDate()).padStart(2, '0');
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const y = now.getFullYear();
    const hh = String(now.getHours()).padStart(2, '0');
    const mm = String(now.getMinutes()).padStart(2, '0');
    return `${d}.${m}.${y} ${hh}:${mm}`;
};

/* ===== OFFLINE DB ===== */
const DB_NAME = 'WorkerAppDB';
const STORE_NAME = 'offlineReports';

const openDB = () => {
  if (!window.indexedDB) return null;
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

const saveOfflineReport = async (reportData) => {
  try {
    const db = await openDB();
    if (!db) return;
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      store.add(reportData);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (e) { console.error(e); }
};

const getOfflineReports = async () => {
  try {
    const db = await openDB();
    if (!db) return [];
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  } catch (e) { return []; }
};

const deleteOfflineReport = async (id) => {
  try {
    const db = await openDB();
    if (!db) return;
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      store.delete(id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (e) { console.error(e); }
};

/* ===== PAGRINDINIS KOMPONENTAS ===== */
const WorkerBeweissicherung = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const sigCanvas = useRef(null);       
  const clientSigCanvas = useRef(null); 
  const reportRef = useRef(null);
  
  const workerId = localStorage.getItem("workerId");
  const workerName = localStorage.getItem("workerName");
  const masterAdminId = localStorage.getItem("masterAdminId");

  /* --- STATE --- */
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  
  // NAUJA: Atskiri laukai rankiniam įvedimui
  const [customProjectName, setCustomProjectName] = useState("");
  const [customProjectAddress, setCustomProjectAddress] = useState("");
  
  const [generalComment, setGeneralComment] = useState("");
  const [images, setImages] = useState([]); 
  const [loading, setLoading] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [syncing, setSyncing] = useState(false);
  const [adminCompanyInfo, setAdminCompanyInfo] = useState({ name: "", address: "" });

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [generatedFile, setGeneratedFile] = useState(null);
  const [hasShared, setHasShared] = useState(false);
  
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [localHistory, setLocalHistory] = useState([]);
  const [viewHistoryItem, setViewHistoryItem] = useState(null);

  const [workerSigActive, setWorkerSigActive] = useState(false);
  const [clientSigActive, setClientSigActive] = useState(false);

  const [overrideReportData, setOverrideReportData] = useState(null);

  useEffect(() => {
    const handleStatusChange = () => {
      setIsOnline(navigator.onLine);
      if (navigator.onLine) processOfflineQueue();
    };
    window.addEventListener('online', handleStatusChange);
    window.addEventListener('offline', handleStatusChange);

    if (!masterAdminId) return;

    const fetchAdminInfo = async () => {
        try {
            const userDoc = await getDoc(doc(db, "users", masterAdminId));
            if (userDoc.exists()) {
                const data = userDoc.data();
                setAdminCompanyInfo({
                    name: data.companyName || "BuildForce AL",
                    address: formatAddress(data.address)
                });
            }
        } catch (e) { console.error("Admin info error:", e); }
    };

    const fetchProjects = async () => {
      try {
        const q = query(collection(db, "projects"), where("adminId", "==", masterAdminId));
        const querySnapshot = await getDocs(q);
        
        const projList = querySnapshot.docs.map(doc => {
            const data = doc.data();
            return {
                id: doc.id,
                name: data.info?.bauvorhaben || data.name || "Project",
                address: formatAddress(data.info?.adresse || data.address || ""),
                customer: data.info?.auftraggeber || data.client || "" 
            };
        });
        setProjects(projList);
      } catch (error) { console.error("Projects error:", error); }
    };

    fetchAdminInfo();
    fetchProjects();
    loadLocalHistory();
    if(navigator.onLine) processOfflineQueue();

    return () => {
      window.removeEventListener('online', handleStatusChange);
      window.removeEventListener('offline', handleStatusChange);
    };
  }, [masterAdminId]);

  const processOfflineQueue = async () => {
    const offlineItems = await getOfflineReports();
    if (offlineItems.length === 0) return;
    setSyncing(true);
    for (const item of offlineItems) {
      try {
        const uploadedUrls = [];
        for (const imgData of item.imagesData) {
           const storageRef = ref(storage, `beweissicherung/${item.adminId}/${Date.now()}_offline.jpg`);
           await uploadBytes(storageRef, imgData.file);
           const url = await getDownloadURL(storageRef);
           uploadedUrls.push({ 
               url: url, 
               comment: imgData.comment,
               uploadedAt: imgData.uploadedAt,
               isArchive: imgData.isArchive
           });
        }
        await addDoc(collection(db, "project_reports"), {
          ...item.firestoreData,
          imageUrl: uploadedUrls[0]?.url || "", 
          allImages: uploadedUrls, 
          timestamp: serverTimestamp(),
          status: "new"
        });
        await deleteOfflineReport(item.id);
      } catch (err) { console.error("Sync error:", err); }
    }
    setSyncing(false);
    loadLocalHistory();
  };

  const handleCameraCapture = (e) => {
    if (e.target.files) {
      const currentTime = getCurrentDateTimeFormatted(); 
      const newFiles = Array.from(e.target.files).map(file => ({
          file: file,
          preview: URL.createObjectURL(file),
          comment: "",
          uploadedAt: currentTime, 
          isArchive: false
      }));
      setImages(prev => [...prev, ...newFiles]);
    }
  };

  const handleGallerySelect = (e) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files).map(file => ({
          file: file,
          preview: URL.createObjectURL(file),
          comment: "",
          uploadedAt: null, 
          isArchive: true
      }));
      setImages(prev => [...prev, ...newFiles]);
    }
  };

  const updateImageComment = (index, text) => {
    const updated = [...images];
    updated[index].comment = text;
    setImages(updated);
  };

  const removeImage = (index) => {
      setImages(prev => prev.filter((_, i) => i !== index));
  };

  const clearSignature = () => { 
      sigCanvas.current.clear(); 
      setWorkerSigActive(false); 
  };
  const clearClientSignature = () => { 
      clientSigCanvas.current.clear(); 
      setClientSigActive(false);
  };

  const loadLocalHistory = () => {
      try {
          const saved = localStorage.getItem("worker_report_history");
          if (saved) setLocalHistory(JSON.parse(saved));
      } catch (e) {
          localStorage.removeItem("worker_report_history");
          setLocalHistory([]);
      }
  };

  const saveToHistoryLog = (projName, projAddr, commentText, imgCount, savedImages = [], isOffline = false, sigUrl = null, clientSigUrl = null) => {
      const newItem = {
          id: Date.now(),
          date: getCurrentDateTimeFormatted(), 
          project: projName,
          address: projAddr,
          comment: commentText,
          imageCount: imgCount,
          savedImages: savedImages, 
          signatureUrl: sigUrl,
          clientSignatureUrl: clientSigUrl,
          statusKey: isOffline ? "offline_mode" : "sent_success"
      };
      const newHistory = [newItem, ...localHistory].slice(0, 20);
      setLocalHistory(newHistory);
      localStorage.setItem("worker_report_history", JSON.stringify(newHistory));
  };

  const deleteLocalHistoryItem = (e, id) => {
      e.stopPropagation(); 
      const newHistory = localHistory.filter(item => item.id !== id);
      setLocalHistory(newHistory);
      localStorage.setItem("worker_report_history", JSON.stringify(newHistory));
  };

  const handleSubmit = async () => {
    if (!masterAdminId) return alert(t('error_no_admin') || "Error: No Admin");
    if (!selectedProject && !customProjectName.trim()) return alert(t('error_select_project') || "Error: Select Project");
    if (images.length === 0) return alert(t('error_no_photos') || "Error: No photos");
    
    if (sigCanvas.current.isEmpty()) return alert(t('error_no_signature') || "Error: No signature");

    setLoading(true);

    let finalProjectName = "";
    let finalProjectId = "";
    let finalAddress = "";
    let finalCustomer = "";

    if (selectedProject) {
        const proj = projects.find(p => p.id === selectedProject);
        finalProjectName = proj ? proj.name : "Unknown";
        finalAddress = proj ? proj.address : ""; 
        finalCustomer = proj ? proj.customer : "";
        finalProjectId = selectedProject;
    } else {
        // PAKEITIMAS: Imame pavadinimą ir adresą iš atskirų laukų
        finalProjectName = customProjectName;
        finalAddress = customProjectAddress; 
        finalProjectId = "manual_entry";
    }

    const signatureURL = sigCanvas.current.toDataURL();
    const clientSignatureURL = clientSigCanvas.current.isEmpty() ? null : clientSigCanvas.current.toDataURL();

    if (isOnline) {
        try {
            const uploadedImageUrls = [];
            for (const imgObj of images) {
                const storageRef = ref(storage, `beweissicherung/${masterAdminId}/${Date.now()}_${imgObj.file.name}`);
                await uploadBytes(storageRef, imgObj.file);
                const url = await getDownloadURL(storageRef);
                uploadedImageUrls.push({ 
                    url: url, 
                    comment: imgObj.comment,
                    uploadedAt: imgObj.uploadedAt,
                    isArchive: imgObj.isArchive
                });
            }

            await addDoc(collection(db, "project_reports"), {
                adminId: masterAdminId,
                projectId: finalProjectId,
                projectName: finalProjectName,
                address: finalAddress,
                customerName: finalCustomer,
                workerId: workerId || "unknown",
                workerName: workerName || "Worker",
                type: "Beweissicherung",
                comment: generalComment,
                imageUrl: uploadedImageUrls[0].url,
                allImages: uploadedImageUrls,
                signatureUrl: signatureURL,
                clientSignatureUrl: clientSignatureURL, 
                timestamp: serverTimestamp(),
                status: "new"
            });

            saveToHistoryLog(finalProjectName, finalAddress, generalComment, images.length, uploadedImageUrls, false, signatureURL, clientSignatureURL);
            generateWhatsappImage(); 

        } catch (error) {
            console.error("Error:", error);
            alert(t('error_sending_failed') || "Error");
            setLoading(false);
        }
    } else {
        try {
            const reportData = {
                adminId: masterAdminId,
                imagesData: images.map(img => ({ 
                    file: img.file, 
                    comment: img.comment, 
                    uploadedAt: img.uploadedAt,
                    isArchive: img.isArchive
                })),
                firestoreData: {
                    adminId: masterAdminId,
                    projectId: finalProjectId,
                    projectName: finalProjectName,
                    address: finalAddress,
                    customerName: finalCustomer,
                    workerId: workerId || "unknown",
                    workerName: workerName || "Worker",
                    type: "Beweissicherung",
                    comment: generalComment,
                    signatureUrl: signatureURL,
                    clientSignatureUrl: clientSignatureURL,
                },
                createdAt: Date.now()
            };

            await saveOfflineReport(reportData);
            saveToHistoryLog(finalProjectName, finalAddress, generalComment, images.length, [], true, signatureURL, clientSignatureURL);
            alert(t('saved_offline_success'));
            navigate(-1);
        } catch (e) {
            console.error(e);
            alert(t('error_save_local'));
        } finally {
            setLoading(false);
        }
    }
  };

  const generateWhatsappImage = () => {
      setTimeout(() => {
          if (reportRef.current) {
              const translatedPrefix = t('report_filename') || "Report";
              
              let currentProjName = customProjectName;
              if (overrideReportData) {
                  currentProjName = overrideReportData.project;
              } else if (selectedProject) {
                  const p = projects.find(pr => pr.id === selectedProject);
                  if(p) currentProjName = p.name;
              }

              // Išvalome pavadinimą
              const safeProjectName = (currentProjName || "").replace(/[\/\?<>\\:\*\|":]/g, "").replace(/\s+/g, "_");
              const fullFileName = `${translatedPrefix}_${safeProjectName}.pdf`;

              const element = reportRef.current;
              const opt = {
                  margin:       [10, 10, 15, 10], 
                  filename:     fullFileName, 
                  image:        { type: 'jpeg', quality: 0.98 }, 
                  html2canvas:  { scale: 2, useCORS: true, letterRendering: true, backgroundColor: "#ffffff", windowWidth: 794 },
                  jsPDF:        { unit: 'mm', format: 'a4', orientation: 'portrait' },
                  pagebreak:    { mode: ['avoid-all', 'css', 'legacy'] } 
              };

              html2pdf().set(opt).from(element).toPdf().get('pdf').then((pdf) => {
                  const totalPages = pdf.internal.getNumberOfPages();
                  for (let i = 1; i <= totalPages; i++) {
                      pdf.setPage(i);
                      pdf.setFontSize(10);
                      pdf.setTextColor(100);
                      const pageText = `${t('page') || 'Page'} ${i} / ${totalPages}`;
                      const pageWidth = pdf.internal.pageSize.getWidth();
                      const pageHeight = pdf.internal.pageSize.getHeight();
                      pdf.text(pageText, pageWidth - 25, pageHeight - 10);
                  }
              }).output('blob').then((blob) => {
                  const file = new File([blob], fullFileName, { type: "application/pdf" });
                  setGeneratedFile(file);
                  
                  if(overrideReportData) {
                      setOverrideReportData(null); 
                      setTimeout(() => {
                          handleShareInternal(file);
                      }, 100);
                  } else {
                      setShowSuccessModal(true);
                      setLoading(false);
                  }

              }).catch(err => {
                  console.error("PDF Error:", err);
                  setLoading(false);
                  if(!overrideReportData) setShowSuccessModal(true); 
              });
          }
      }, 1000); 
  };

  const handleShareInternal = async (fileObj) => {
    // Failo pavadinimas imamas iš sugeneruoto failo
    const fileName = fileObj.name || `${t('report_filename')}.pdf`;
    const shareTitle = fileName.replace('.pdf', '');

    if (fileObj) {
        try {
            const reader = new FileReader();
            reader.readAsDataURL(fileObj);
            reader.onloadend = async () => {
                const base64data = reader.result; 
                const result = await Filesystem.writeFile({
                    path: fileName,
                    data: base64data,
                    directory: Directory.Cache
                });
                await Share.share({
                    title: shareTitle,
                    text: shareTitle,
                    url: result.uri, 
                    dialogTitle: shareTitle,
                });

                setHasShared(true);
                setTimeout(() => setHasShared(false), 3000);
            };
        } catch (error) {
            console.error("Share failed:", error);
            alert(t('error') + ": " + error.message);
        }
    }
  };

  const handleShare = async () => {
      await handleShareInternal(generatedFile);
  };

  const handleShareFromHistory = (item) => {
      setOverrideReportData(item); 
      setLoading(true);
      generateWhatsappImage(); 
  };

  /* =======================================================
      REPORT TEMPLATE
      ======================================================= */
  const ReportTemplate = () => {
      const data = overrideReportData || {
          projectName: selectedProject ? projects.find(p => p.id === selectedProject)?.name : customProjectName,
          // PAKEITIMAS: Jei nėra pasirinkto projekto, imame customProjectAddress
          projectAddress: selectedProject ? projects.find(p => p.id === selectedProject)?.address : customProjectAddress,
          customerName: selectedProject ? projects.find(p => p.id === selectedProject)?.customer : "",
          date: getCurrentDateTimeFormatted(),
          images: images.map(img => ({ url: img.preview, comment: img.comment, uploadedAt: img.uploadedAt, isArchive: img.isArchive })),
          generalComment: generalComment,
          signatureUrl: sigCanvas.current ? sigCanvas.current.toDataURL() : "",
          clientSignatureUrl: clientSigCanvas.current?.isEmpty() === false ? clientSigCanvas.current.toDataURL() : ""
      };

      if (overrideReportData) {
          data.projectName = overrideReportData.project;
          data.projectAddress = overrideReportData.address;
          data.customerName = ""; 
          data.date = overrideReportData.date;
          data.generalComment = overrideReportData.comment;
          data.signatureUrl = overrideReportData.signatureUrl;
          data.clientSignatureUrl = overrideReportData.clientSignatureUrl;
          data.images = overrideReportData.savedImages || [];
      }

      return (
        <div style={{ position: 'absolute', top: 0, left: 0, zIndex: -1000, width: '100%', overflow: 'hidden', height: 0 }}>
          <div ref={reportRef} style={{
              width: '794px', 
              backgroundColor: '#ffffff', 
              color: '#000000', 
              padding: '40px 40px 0 40px', 
              fontFamily: 'Arial, sans-serif', 
              fontSize: '16px',
              lineHeight: '1.5'
          }}>
              <div style={{textAlign: 'center', marginBottom: '30px', borderBottom: '2px solid #000', paddingBottom: '20px'}}>
                  <h2 style={{margin: '0', fontSize:'28px', textTransform: 'uppercase', fontWeight: 'bold', color: '#000'}}>
                      {adminCompanyInfo.name}
                  </h2>
                  <p style={{margin: '10px 0 0 0', fontSize: '16px', color: '#000'}}>
                      {adminCompanyInfo.address}
                  </p>
              </div>

              <div style={{marginBottom: '30px', border: '1px solid #ccc', padding: '15px', borderRadius: '5px', background: '#f9f9f9', pageBreakInside: 'avoid'}}>
                  <table style={{width: '100%', borderCollapse: 'collapse', color: '#000'}}>
                      <tbody>
                          <tr>
                              <td style={{padding: '5px', fontWeight: 'bold', width: '150px'}}>{t('worker')}:</td>
                              <td style={{padding: '5px'}}>{workerName}</td>
                          </tr>
                          <tr>
                              <td style={{padding: '5px', fontWeight: 'bold'}}>{t('project')}:</td>
                              <td style={{padding: '5px'}}>{data.projectName}</td>
                          </tr>
                          {data.projectAddress && (
                            <tr>
                                <td style={{padding: '5px', fontWeight: 'bold'}}>{t('address')}:</td>
                                <td style={{padding: '5px'}}>{data.projectAddress}</td>
                            </tr>
                          )}
                          {data.customerName && (
                            <tr>
                                <td style={{padding: '5px', fontWeight: 'bold'}}>{t('client')}:</td>
                                <td style={{padding: '5px'}}>{data.customerName}</td>
                            </tr>
                          )}
                          <tr>
                              <td style={{padding: '5px', fontWeight: 'bold'}}>{t('date')}:</td>
                              <td style={{padding: '5px'}}>{data.date}</td>
                          </tr>
                      </tbody>
                  </table>
              </div>

              <div style={{display: 'flex', flexDirection: 'column', gap: '30px', marginBottom: '30px'}}>
                  {data.images.map((img, idx) => (
                      <div key={idx} style={{
                          border: '1px solid #000', 
                          borderRadius: '5px', 
                          background: '#fff', 
                          overflow: 'hidden', 
                          pageBreakInside: 'avoid', 
                          breakInside: 'avoid'
                      }}>
                          <div style={{
                              width: '100%',
                              backgroundColor: '#f5f5f5',
                              borderBottom: '1px solid #000',
                              display: 'flex',
                              justifyContent: 'center',
                              padding: '10px'
                          }}>
                              <img 
                                src={img.url || img.preview} 
                                alt={`Foto ${idx+1}`} 
                                style={{ maxWidth: '100%', maxHeight: '600px', objectFit: 'contain' }} 
                              />
                          </div>
                          <div style={{padding: '15px'}}>
                              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                  <p style={{margin: '0', fontWeight: 'bold', fontSize: '14px', color: '#000'}}>{t('photo_num')} #{idx + 1}</p>
                                  {/* paddingRight laiko pataisymas */}
                                  {img.uploadedAt ? (
                                      <span style={{fontSize: '11px', color: '#333', fontStyle: 'italic', whiteSpace: 'nowrap', marginRight: '15px', paddingRight: '10px'}}>{img.uploadedAt}</span>
                                  ) : img.isArchive ? (
                                      <span style={{fontSize: '11px', color: '#333', fontStyle: 'italic', whiteSpace: 'nowrap', marginRight: '15px', paddingRight: '10px'}}>{t('from_archive')}</span>
                                  ) : null}
                              </div>
                              {img.comment && <p style={{margin: '5px 0 0 0', fontStyle: 'italic', color: '#000'}}>{img.comment}</p>}
                          </div>
                      </div>
                  ))}
              </div>

              <div style={{marginBottom: '40px', backgroundColor: '#f4f4f4', padding: '20px', borderRadius: '5px', border: '1px solid #000', pageBreakInside: 'avoid'}}>
                  <p style={{fontWeight: 'bold', margin: '0 0 10px 0', textTransform: 'uppercase', fontSize: '14px', color: '#000'}}>
                      {t('general_comment_uppercase')}:
                  </p>
                  <p style={{margin: 0, fontSize: '16px', whiteSpace: 'pre-wrap', color: '#000'}}>{data.generalComment || "-"}</p>
              </div>

              <div style={{display: 'flex', justifyContent: 'space-between', marginTop: '50px', pageBreakInside: 'avoid'}}>
                  <div style={{textAlign: 'center', width: '200px'}}>
                      {data.signatureUrl && <img src={data.signatureUrl} alt="Sign" style={{height: '80px', marginBottom: '10px'}} />}
                      <div style={{borderTop: '1px solid #000', paddingTop: '5px', fontSize: '14px', color: '#000', fontWeight: 'bold'}}>
                          {t('signature')}
                      </div>
                  </div>
                  {data.clientSignatureUrl && (
                      <div style={{textAlign: 'center', width: '200px'}}>
                          <img src={data.clientSignatureUrl} alt="Sign" style={{height: '80px', marginBottom: '10px'}} />
                          <div style={{borderTop: '1px solid #000', paddingTop: '5px', fontSize: '14px', color: '#000', fontWeight: 'bold'}}>
                              {t('verified_by')}
                          </div>
                      </div>
                  )}
              </div>
          </div>
        </div>
      );
  };

  return (
    <div className="wb-container">
      <ReportTemplate />

      <div className="wb-header">
         <div style={{display:'flex', alignItems:'center'}}>
            <button className="wb-gold-back-btn" onClick={() => navigate(-1)}>
                ← {t('back')}
            </button>
         </div>
         <h2 className="wb-title">{t('reports_title')}</h2>
         <button onClick={() => setShowHistoryModal(true)} className="wb-history-btn">
             {t('history')}
         </button>
      </div>

      {syncing && <div className="wb-sync-bar">🔄...</div>}
      {!isOnline && <div className="wb-offline-bar">⚠️ Offline</div>}

      <div className="wb-form-group">
        <label className="wb-label">{t('project')}</label>
        <select 
          className="wb-select" value={selectedProject} 
          onChange={(e) => { 
              setSelectedProject(e.target.value); 
              setCustomProjectName(""); 
              setCustomProjectAddress(""); // Išvalome, jei pasirenkam iš sąrašo
          }}
        >
          <option value="">-- {t('select_option')} --</option>
          {projects.map(p => (<option key={p.id} value={p.id}>{p.name}</option>))}
        </select>
        
        {selectedProject && (
            <div style={{marginTop: 15, padding: 15, background: '#161b22', borderRadius: 12, border: '1px solid #30363d'}}>
                <div style={{color:'#00e6b8', fontWeight:'bold', fontSize: 16, marginBottom: 5}}>
                    {projects.find(p => p.id === selectedProject)?.name}
                </div>
                <div style={{color:'#8b949e', fontSize: 14}}>
                    📍 {projects.find(p => p.id === selectedProject)?.address}
                </div>
            </div>
        )}

        {/* PAKEITIMAS: Rodo du atskirus laukus rankiniam įvedimui */}
        {!selectedProject && (
             <div style={{display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '10px'}}>
                 <input type="text" className="wb-select"
                    placeholder={t('or_type_manually') || t('project')} 
                    value={customProjectName} 
                    onChange={(e) => { setCustomProjectName(e.target.value); setSelectedProject(""); }}
                 />
                 <input type="text" className="wb-select"
                    placeholder={t('address')}
                    value={customProjectAddress} 
                    onChange={(e) => setCustomProjectAddress(e.target.value)}
                 />
             </div>
        )}
      </div>

      <div className="wb-form-group">
        <div className="wb-list-header">
            <label className="wb-label">{t('photos')} ({images.length})</label>
            
            <input 
                type="file" 
                id="cameraInput" 
                capture="environment" 
                accept="image/*" 
                onChange={handleCameraCapture} 
                style={{display:'none'}} 
            />
            <input 
                type="file" 
                id="galleryInput" 
                multiple 
                accept="image/*" 
                onChange={handleGallerySelect} 
                style={{display:'none'}} 
            />
        </div>
        
        <div className="wb-photo-list">
            {images.length === 0 && <div className="wb-empty">{t('no_photos_yet')}</div>}
            {images.map((img, idx) => (
                <div key={idx} className="wb-list-item">
                    <div className="wb-pli-image">
                        <img src={img.preview} alt="Foto" />
                        <button className="wb-pli-delete" onClick={() => removeImage(idx)}>✕</button>
                    </div>
                    <div className="wb-pli-content">
                        <div className="wb-pli-number" style={{display:'flex', justifyContent:'space-between'}}>
                            <span>{t('photo_num')} #{idx + 1}</span>
                            <span style={{fontSize:'11px', color:'#00e6b8'}}>
                                {img.uploadedAt || (img.isArchive ? "🗂️" : "")}
                            </span>
                        </div>
                        <textarea className="wb-input" placeholder={t('photo_comment_placeholder')}
                            value={img.comment} onChange={(e) => updateImageComment(idx, e.target.value)} rows="2"
                        />
                    </div>
                </div>
            ))}
        </div>

        <div style={{display: 'flex', gap: '10px', marginTop: '15px'}}>
            <label htmlFor="cameraInput" className="wb-add-photo-btn-large" style={{
                flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', 
                padding: '15px', background: '#21262d', border: '1px dashed #30363d', borderRadius: '12px', 
                color: '#00e6b8', cursor: 'pointer', transition: 'all 0.2s ease'
            }}>
                <span style={{fontSize: '24px', marginBottom: '5px'}}>📷</span>
                <span style={{fontWeight: 'bold', fontSize: '14px'}}>{t('camera')}</span>
            </label>

            <label htmlFor="galleryInput" className="wb-add-photo-btn-large" style={{
                flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', 
                padding: '15px', background: '#21262d', border: '1px dashed #30363d', borderRadius: '12px', 
                color: '#38bdf8', cursor: 'pointer', transition: 'all 0.2s ease'
            }}>
                <span style={{fontSize: '24px', marginBottom: '5px'}}>🖼️</span>
                <span style={{fontWeight: 'bold', fontSize: '14px'}}>{t('gallery')}</span>
            </label>
        </div>
      </div>

      <div className="wb-form-group">
        <label className="wb-label">{t('general_comment')}</label>
        <textarea className="wb-textarea" value={generalComment} onChange={(e) => setGeneralComment(e.target.value)}
          placeholder="..." rows="3"
        />
      </div>

      <div className="wb-form-group">
        <div className="wb-sig-header">
            <label className="wb-label">{t('signature')}</label>
            <span onClick={clearSignature} className="wb-clear-link">{t('clear')}</span>
        </div>
        <div className="wb-sig-container" style={{position: 'relative', overflow: 'hidden'}}>
            {!workerSigActive && (
                <div onClick={() => setWorkerSigActive(true)} style={{position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, cursor: 'pointer', borderRadius: '4px'}}>
                    <span style={{color: '#fff', fontWeight: 'bold', background: 'rgba(0,0,0,0.5)', padding: '5px 10px', borderRadius: '4px'}}>{t('tap_to_sign')}</span>
                </div>
            )}
            <SignatureCanvas ref={sigCanvas} penColor="black" canvasProps={{ className: 'sigCanvas', width: window.innerWidth - 42, height: 150 }} />
        </div>

        <div className="wb-sig-header" style={{marginTop: '20px'}}>
            <label className="wb-label">{t('client_signature')}</label>
            <span onClick={clearClientSignature} className="wb-clear-link">{t('clear')}</span>
        </div>
        <div className="wb-sig-container" style={{position: 'relative', overflow: 'hidden'}}>
            {!clientSigActive && (
                <div onClick={() => setClientSigActive(true)} style={{position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, cursor: 'pointer', borderRadius: '4px'}}>
                    <span style={{color: '#fff', fontWeight: 'bold', background: 'rgba(0,0,0,0.5)', padding: '5px 10px', borderRadius: '4px'}}>{t('tap_to_sign')}</span>
                </div>
            )}
            <SignatureCanvas ref={clientSigCanvas} penColor="black" canvasProps={{ className: 'sigCanvas', width: window.innerWidth - 42, height: 150 }} />
        </div>
      </div>

      <button onClick={handleSubmit} disabled={loading} className={`wb-submit-btn ${!isOnline ? 'wb-off-btn' : ''}`}>
        {loading ? t('processing') : (!isOnline ? t('save_offline_btn') : t('send_btn'))}
      </button>

      {/* MODALAI */}
      {showHistoryModal && (
        <div className="wb-modal-overlay">
            <div className="wb-modal-content">
                <div className="wb-modal-header">
                    <h3>{t('history')}</h3>
                    <button onClick={() => setShowHistoryModal(false)} className="wb-close">✕</button>
                </div>
                <div className="wb-history-list">
                    {localHistory.length === 0 ? <p style={{textAlign:'center', color:'#888'}}>{t('empty_history')}</p> : (
                        localHistory.map(item => (
                            <div key={item.id} className="wb-history-item" onClick={() => setViewHistoryItem(item)}>
                                <div>
                                    <div className="wb-h-proj">{item.project}</div>
                                    <div className="wb-h-date">{item.date} • {item.imageCount} img.</div>
                                    <div className="wb-h-status">{t(item.statusKey)}</div>
                                </div>
                                <button onClick={(e) => deleteLocalHistoryItem(e, item.id)} className="wb-h-del">✕</button>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
      )}

      {viewHistoryItem && (
        <div className="wb-modal-overlay">
            <div className="wb-modal-content" style={{height: '90%', maxHeight: '90%'}}>
                <div className="wb-modal-header">
                    <h3>{t('report_info')}</h3>
                    <button onClick={() => setViewHistoryItem(null)} className="wb-close">✕</button>
                </div>
                <div style={{color: 'white', padding: '10px', overflowY: 'auto', flex: 1}}>
                    <p style={{marginBottom: '10px'}}><strong>{t('project')}:</strong> <br/> {viewHistoryItem.project}</p>
                    <p style={{marginBottom: '10px'}}><strong>{t('address')}:</strong> <br/> {viewHistoryItem.address || "-"}</p>
                    <p style={{marginBottom: '10px'}}><strong>{t('date')}:</strong> <br/> {viewHistoryItem.date}</p>
                    <p style={{marginBottom: '10px'}}><strong>{t('general_comment')}:</strong> <br/> <span style={{color: '#ccc'}}>{viewHistoryItem.comment || "-"}</span></p>
                    
                    {/* NAUJAS: Share mygtukas istorijoje */}
                    <button 
                        onClick={() => handleShareFromHistory(viewHistoryItem)} 
                        className="wb-whatsapp-btn" 
                        style={{marginBottom: '20px', backgroundColor: '#00e6b8', color: '#000'}}
                    >
                        📄 {t('share')} PDF
                    </button>

                    <div style={{marginTop: '20px', borderTop: '1px solid #333', paddingTop: '10px'}}>
                        <strong>{t('photos')} ({viewHistoryItem.imageCount}):</strong>
                        <div style={{display: 'flex', flexDirection: 'column', gap: '15px', marginTop: '10px'}}>
                            {viewHistoryItem.savedImages && viewHistoryItem.savedImages.length > 0 ? (
                                viewHistoryItem.savedImages.map((img, idx) => (
                                    <div key={idx} style={{background: '#000', padding: '5px', borderRadius: '8px'}}>
                                        <img src={img.url} alt="Foto" style={{width: '100%', borderRadius: '5px'}} />
                                        {img.comment && <p style={{fontSize: '12px', color: '#ccc', margin: '5px 0 0 0'}}><i>{img.comment}</i></p>}
                                    </div>
                                ))
                            ) : (
                                <p style={{color: '#666', fontSize: '12px'}}>(...)</p>
                            )}
                        </div>
                    </div>
                </div>
                <button onClick={() => setViewHistoryItem(null)} className="wb-whatsapp-btn" style={{marginTop: '10px', background: '#333'}}>{t('close')}</button>
            </div>
        </div>
      )}

      {showSuccessModal && (
        <div className="wb-modal-overlay">
            <div className="wb-success-box">
                <h3 style={{color: 'white'}}>{t('sent_success')} ✅</h3>
                
                {/* PAKEISTAS SHARE MYGTUKAS: Rodo žalią 'Shared', jei paspaustas */}
                <button 
                    onClick={handleShare} 
                    className="wb-whatsapp-btn"
                    style={hasShared ? {backgroundColor: 'green', color: 'white'} : {}}
                >
                    {hasShared ? `✅ ${t('shared_success')}` : t('share')}
                </button>
                
                <button onClick={() => navigate(-1)} className="wb-close-modal-btn">{t('close')}</button>
            </div>
        </div>
      )}
    </div>
  );
};

export default WorkerBeweissicherung;